import { useQueryClient } from '@tanstack/react-query';
import { FISCAL_KIND_LABELS, fiscalNoteSchema, type FiscalDocumentDTO, type SaleDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Field, Input, Select, Textarea, useToast } from '@bifeai/ui';
import { FilePlus2, Printer, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatMoney } from '../lib/format';
import { printSale } from '../lib/ticket-print';
import { useSession } from '../lib/session';
import { pesosToCents } from '../lib/units';

const label = (d: FiscalDocumentDTO) => `${FISCAL_KIND_LABELS[d.kind]} ${d.letter}${d.displayNumber ? ` ${d.displayNumber}` : ''}`;

async function reload(saleId: string): Promise<SaleDTO> {
  return api.get<SaleDTO>(`/sales/${saleId}`);
}

/** Estado de la factura de la venta recién cobrada (en el mostrador). */
export function FiscalLine({ sale, onUpdate }: { sale: SaleDTO; onUpdate: (s: SaleDTO) => void }) {
  const doc = sale.fiscal.find((d) => d.kind === 'INVOICE');
  const [busy, setBusy] = useState(false);
  if (!doc) return null;
  async function retry() {
    setBusy(true);
    try {
      await api.post(`/fiscal/documents/${doc!.id}/retry`);
      onUpdate(await reload(sale.id));
    } finally {
      setBusy(false);
    }
  }
  if (doc.status === 'AUTHORIZED') {
    return (
      <div className="bf-cell-sub">
        {label(doc)} · CAE {doc.cae}
        {doc.environment === 'HOMO' && ' · prueba'}
      </div>
    );
  }
  return (
    <Callout tone={doc.status === 'REJECTED' ? 'danger' : 'warning'}>
      {doc.status === 'PENDING'
        ? 'La factura quedó pendiente: ARCA no respondió. Se reintenta sola; cuando salga el CAE, reimprimila desde Ventas.'
        : `ARCA rechazó la factura: ${doc.error ?? ''}`}
      <div style={{ marginTop: 6 }}>
        <Button size="sm" icon={<RotateCw />} loading={busy} onClick={() => void retry()}>
          Reintentar ahora
        </Button>
      </div>
    </Callout>
  );
}

/** Comprobantes electrónicos de una venta (detalle de la venta): estado, reimpresión, reintento y notas. */
export function FiscalDocsPanel({ sale }: { sale: SaleDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<FiscalDocumentDTO | null>(null);
  if (!sale.fiscal.length) return null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['sales'] });

  async function retry(d: FiscalDocumentDTO) {
    setBusy(d.id);
    try {
      const r = await api.post<FiscalDocumentDTO>(`/fiscal/documents/${d.id}/retry`);
      if (r.status === 'AUTHORIZED') toast.success('Comprobante autorizado', `CAE ${r.cae}`);
      else toast.error('Todavía sin CAE', r.error ?? 'ARCA no respondió.');
      await refresh();
    } catch (err) {
      toast.error('No se pudo reintentar', errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bf-form" style={{ gap: 8 }}>
      <span className="bf-label">Facturación electrónica</span>
      {sale.fiscal.map((d) => (
        <div key={d.id} className="bf-pos-row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span>
            <strong>{label(d)}</strong> · {formatMoney(d.totalCents)}{' '}
            {d.status === 'AUTHORIZED' ? (
              <Badge tone="success">CAE {d.cae}</Badge>
            ) : d.status === 'PENDING' ? (
              <Badge tone="warning">Pendiente</Badge>
            ) : (
              <Badge tone="danger">Rechazada</Badge>
            )}
            {d.environment === 'HOMO' && <Badge>prueba</Badge>}
            {d.error && d.status !== 'AUTHORIZED' && <span className="bf-cell-sub"> {d.error}</span>}
            {d.reason && d.kind !== 'INVOICE' && <span className="bf-cell-sub"> {d.reason}</span>}
          </span>
          <span style={{ display: 'flex', gap: 6 }}>
            {d.status === 'AUTHORIZED' ? (
              <Button size="sm" icon={<Printer />} onClick={() => printSale(sale, sale.print.ticket, d)}>
                Imprimir
              </Button>
            ) : (
              <Button size="sm" icon={<RotateCw />} loading={busy === d.id} onClick={() => void retry(d)}>
                Reintentar
              </Button>
            )}
            {d.kind === 'INVOICE' && d.status === 'AUTHORIZED' && can('sales.void') && (
              <Button size="sm" variant="ghost" icon={<FilePlus2 />} onClick={() => setNoteFor(noteFor ? null : d)}>
                Nota de crédito / débito
              </Button>
            )}
          </span>
        </div>
      ))}
      {noteFor && <NoteForm invoice={noteFor} onDone={() => setNoteFor(null)} />}
    </div>
  );
}

function NoteForm({ invoice, onDone }: { invoice: FiscalDocumentDTO; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [kind, setKind] = useState<'CREDIT_NOTE' | 'DEBIT_NOTE'>('CREDIT_NOTE');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const parsed = fiscalNoteSchema.safeParse({ kind, amountCents: pesosToCents(amount) ?? 0, reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Revisá los datos.');
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<FiscalDocumentDTO>(`/fiscal/documents/${invoice.id}/notes`, parsed.data);
      if (r.status === 'AUTHORIZED') toast.success(`${FISCAL_KIND_LABELS[r.kind]} autorizada`, `CAE ${r.cae}`);
      else toast.error(`${FISCAL_KIND_LABELS[r.kind]} sin CAE todavía`, r.error ?? 'Se reintenta sola.');
      await qc.invalidateQueries({ queryKey: ['sales'] });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bf-form" style={{ gap: 10 }}>
      <Callout>
        Nota de crédito: devolución o descuento posterior (resta). Nota de débito: un cargo que faltó cobrar (suma). Se asocia a la {label(invoice)}.
      </Callout>
      <div className="bf-form-row">
        <Field label="Tipo">
          <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="CREDIT_NOTE">Nota de crédito</option>
            <option value="DEBIT_NOTE">Nota de débito</option>
          </Select>
        </Field>
        <Field label="Importe (con IVA)">
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="$ 0" />
        </Field>
      </div>
      <Field label="Motivo" error={error ?? undefined}>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ej.: devolvió 1 kg de asado" />
      </Field>
      <div className="bf-form-actions">
        <Button onClick={onDone} disabled={busy}>
          Cancelar
        </Button>
        <Button variant="primary" loading={busy} onClick={() => void submit()}>
          Emitir
        </Button>
      </div>
    </div>
  );
}
